//================================================================
//  Tab: Rejected
//================================================================

//  Purpose: This tab displays all the current user's requests that are rejected

//  Properties:
//    - requests = {An array (useState) - this is the list of all the business requests that are rejected for the current user}
//    - setRequests = {useState, used to set the requests state}

//  Example:
//    <Rejected
//      requests={requests}
//      setRequests={setRequests}
//    ></Rejected>    

//================================================================

//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../Components/PageComponent/PageComponent';
import ExportToCSV from '../../../Components/ExportToCSV/ExportToCSV';
import AssignTask from '../../../Components/AssignTask/AssignTask';

//Functions
import QueryListener from '../../../Library/QueryListener';
import ConvertDate from '../../../Library/ConvertDate';

//Images
import IconSearch from '../../../Components/Images/Icon_Search_Grey.svg';

export default function Rejected({
  requests, 
  setRequests
}) {

  //------------------------------------------------------
  //  useContext and React Router
  //------------------------------------------------------
  
    const getUser = useContext(GetUser);
    const navigate = useNavigate();

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

    // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
    const [pageStatus, setPageStatus] = useState();
    
    // Search Input
    const [searchInput, setSearchInput] = useState('');

    // Current Filter > 'Commercial', 'Demand Management', 'Procurement' & 'Approval'
    const [currentFilter, setCurrentFilter] = useState('All');

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

    // Handles search function
    function handleSearch(value) {

      setSearchInput(value);

      // Reset the Filter
      if (value.length === 0) {
        setRequests({ 'rejectedRequestsFiltered': requests.rejectedRequests });

      };

      // Search Filter
      //  1. Application Name
      //  2. Initiative Name
      //  3. Assigned To
      //  4. Request ID
      const searchResults = requests.rejectedRequests.filter((object) =>
        object?.applicationname?.toLowerCase().includes(value.toLowerCase()) ||
        object?.initiativename?.toLowerCase().includes(value.toLowerCase()) || 
        object?.assignedto?.email?.toLowerCase().includes(value.toLowerCase())||
        object?.parentrequestid?.toLowerCase().includes(value.toLowerCase())
      );

      // Save the filtered search results to the useState
      setRequests({ 'rejectedRequestsFiltered': searchResults });

    };

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

    // Query Listener
    // - Get all business requests that are rejected
    useEffect(() => {
         
      if (currentFilter === undefined) return;

      function onLoadChange(documents){
        
        // Sort by Created Date (Newest to Oldest)
        documents.sort(function(a, b) {
          return b.createddate - a.createddate;
        });
      
        setRequests({ 'rejectedRequests': documents });
        setRequests({ 'rejectedRequestsFiltered': documents });
        
      }
      
      function onError(error){
        console.log(error);
        setPageStatus('error-fatal');
      }

      const unsubscribe = QueryListener('requests', 
        currentFilter === 'All' ? 
        [
          ['assignmentgroup', '==', 'Demand Management'],
          ['tasktype', '==', 'Digital Request'],
          ['status', '==', 'Rejected'],
          ['assignedto.email', '==', getUser?.emailaddress]
        ]
        :
        [
          ['assignmentgroup', '==', 'Demand Management'],
          ['tasktype', '==', 'Digital Request'],
          ['status', '==', 'Rejected'],
          ['assignedto.email', '==', getUser?.emailaddress],
          ['requesttype', '==', currentFilter]
        ], 
        onLoadChange, onLoadChange, onError);
      
      return () =>{
        unsubscribe();
      };
        
    // eslint-disable-next-line
    }, [currentFilter, getUser])
  
  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

    return (
      <PageComponent
        status={pageStatus}
        body={
          <div className='flex flex-col px-[15px] gap-[20px]'>

            <div className='flex flex-row justify-between gap-3'>

              {/* ======== Status (Buttons) ========== */}
              <div className='flex flex-row justify-start gap-2'>

                {/* All */}
                <button className={currentFilter === 'All' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('All')}>
                  All
                </button>

                {/* New Initiative or Technology */}
                <button className={currentFilter === 'New Initiative or Technology' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('New Initiative or Technology')}>
                  New Initiative or Technology
                </button>

                {/* Contract Renewal */}
                <button className={currentFilter === 'Contract Renewal' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Contract Renewal')}>
                  Contract Renewal
                </button>

                {/* Enhancement */}
                <button className={currentFilter === 'Enhancement' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Enhancement')}>
                  Enhancement
                </button>

                {/* Additional Licenses or Overages */}
                <button className={currentFilter === 'Additional Licenses or Overages' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Additional Licenses or Overages')}>
                  Additional Licenses or Overages
                </button>

                {/* Decommission */}
                <button className={currentFilter === 'Decommission' ? 'Filter-Primary' : 'Filter-Secondary'} onClick={() => setCurrentFilter('Decommission')}>
                  Decommission
                </button>

              </div>

              {/* ----------------------------------- */}
              {/*  Export                             */}
              {/* ----------------------------------- */}

              <div className='flex flex-row justify-end items-center gap-3'>

                {/* Download Report */}
                <ExportToCSV
                  filename={`Rejected-${Date.now()}`}
                  data={requests?.rejectedRequestsFiltered}
                ></ExportToCSV>

              </div>

            </div>

            {/* ----------------------------------- */}
            {/*  Requests Table                     */}
            {/* ----------------------------------- */}

            <div className='Table-Container'>

              {/* ======== Sorting and Filters ========== */}
              <div className='flex flex-row justify-between py-[10px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

              <label className='font-semibold self-center'>
                  Total: {requests?.rejectedRequestsFiltered.length} of {requests?.rejectedRequests.length}
                </label>

                {/* Search Bar */}
                <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] rounded-[5px] border-1 border-solid border-[#dee2e6] w-[300px]'>
                  
                  <img className='ml-[10px]' src={IconSearch} alt='searchInput-icon'></img>
                  <label htmlFor='searchInput'>
                    <input
                        id='searchInput'
                        className='border-none h-[40px] p-0 m-0 outline-none'
                        type='text'
                        placeholder='Search'
                        onChange={(e) => handleSearch(e.target.value)}
                        value={searchInput}
                    ></input>
                  </label>
                </div>

              </div>

              <div className='overflow-y-auto h-[calc(100vh-360px)]'>
                <table className='min-w-full max-h-96 table-auto'>
                  <thead>
                    <tr className='sticky top-0 bg-white shadow-md z-10'>
                      <th className='py-[15px] px-[20px] font-medium'>Request ID</th>
                      <th className='py-[15px] px-[20px] font-medium'>Created</th>
                      <th className='py-[15px] px-[20px] font-medium'>Requester</th>
                      <th className='py-[15px] px-[20px] font-medium'>Initiative Name</th>
                      <th className='py-[15px] px-[20px] font-medium'>Application Name</th>    
                      <th className='py-[15px] px-[20px] font-medium'>Primary Beneficiary</th>
                      <th className='py-[15px] px-[20px] font-medium'>Request Type</th>
                      <th className='py-[15px] px-[20px] font-medium'>Assigned To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      // No requests found
                      requests?.rejectedRequestsFiltered?.length === 0 ? 
                      (
                        <tr>
                          <td colSpan={7}>
                            <div className='text-center p-[2%]'>
                              No requests found.
                            </div>
                          </td>
                        </tr>
                      )
                      : 
                      (
                        // Map in each business request
                        requests?.rejectedRequestsFiltered?.map((request, index) => (
                          <tr key={index} className='border-b-[#C8C8C9] border-solid border-b last:border-0 hover:bg-[#F0F7F7]'>

                            {/* Request ID */}
                            <td 
                              className='py-[15px] px-[20px] font-medium uppercase cursor-pointer hover:underline'
                              onClick={() => navigate(`/requests/${request?.requestid}`)}
                            >
                              {request?.parentrequestid}
                            </td>

                            {/* Created Date */}
                            <td className='py-[15px] px-[20px]'>{ConvertDate(request?.createddate)}</td>

                            {/* Requester Name */}
                            <td className='py-[15px] px-[20px] capitalize'>
                              {request?.createdby?.givenname} {request?.createdby?.surname}
                            </td>

                            {/* Initiative Name */}
                            <td className='py-[15px] px-[20px]'>
                              {request?.initiativename}
                            </td>

                            {/* Application Name */}
                            <td className='py-[15px] px-[20px]'>
                              {
                                request?.applicationname?.length === 0 ? '-' : request?.applicationname
                              }
                            </td>

                            {/* Primary Beneficiary */}
                            <td className='pt-[10px] pb-[5px] px-[20px]'>
                              <div>
                                {
                                  [
                                    {'name': 'Investment Management', 'background': '#4B7BEC', 'color': 'white', 'bordercolor': '#4B7BEC', 'primaryusers': 'Investment Management'},
                                    {'name': 'Investments', 'background': '#45AAF2', 'color': 'white', 'bordercolor': '#45AAF2', 'primaryusers': 'Investments'},
                                    {'name': 'Construction', 'background': '#028E8C', 'color': 'white', 'bordercolor': '#028E8C', 'primaryusers': 'Construction'}, 
                                    {'name': 'Development', 'background': '#8ABB45', 'color': 'white', 'bordercolor': '#8ABB45', 'primaryusers': 'Development'},
                                    {'name': 'Functions', 'background': '#FD9644', 'color': 'white', 'bordercolor': '#FD9644', 'primaryusers': 'Functions'},
                                    {'name': 'Digital', 'background': '#A65EEA', 'color': 'white', 'bordercolor': '#A65EEA', 'primaryusers': 'Digital'},
                                    {'name': 'Defence', 'background': '#0CBDBA', 'color': 'white', 'bordercolor': '#0CBDBA', 'primaryusers': 'Defence'},

                                  ].filter((style) => style?.primaryusers === request?.primaryusers).map((object, index) => (
                                    <label key={index} className='Status-Label' style={{backgroundColor: `${object.background}`, color: `${object.color}`, border: `1px solid ${object.bordercolor}`}} >
                                      {object.name}
                                    </label>
                                  ))
                                }
                              </div>
                            </td>

                            {/* Request Type */}
                            <td className='py-[15px] px-[20px]'>
                              {request?.requesttype}
                            </td>

                            {/* Assigned To */}
                            <td className='py-[15px] px-[10px]'>
                            < AssignTask
                                query={['roles.businessPartnerUser', '==', true]}
                                currentRequest={request}
                                disabled
                              ></AssignTask>
                            </td>

                          </tr>
                        ))
                      )
                    }
                  </tbody>
                </table>
              </div>

            </div>

          </div>
        }
      ></PageComponent>
    )
}